import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { paymentsAPI } from '../services/api';

const PaymentResult = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user, logout } = useAuth();
  
  const [status, setStatus] = useState('loading'); // loading, success, pending, failed
  const [error, setError] = useState(null);
  
  const orderId = searchParams.get('order');
  
  const translations = {
    es: {
      title: 'Resultado del Pago',
      loading: 'Verificando estado del pago...',
      success: {
        title: '¡Pago Exitoso!',
        description: 'Tu suscripción anual ha sido activada correctamente.',
        detail: 'Ya puedes acceder a todas las funcionalidades de tu cuenta de Organización.',
        cta: 'Ir al Dashboard'
      },
      pending: {
        title: 'Pago Pendiente',
        description: 'Tu pago está siendo procesado.',
        detail: 'Por favor espera unos momentos y actualiza esta página. Si el problema persiste, contáctanos.',
        cta: 'Actualizar Estado'
      },
      failed: {
        title: 'Pago No Completado',
        description: 'No pudimos procesar tu pago.',
        detail: 'Puedes intentar nuevamente o contactarnos para asistencia.',
        cta: 'Reintentar Pago',
        logout: 'Volver al Inicio'
      },
      noOrder: {
        title: 'Sin Información de Pago',
        description: 'No encontramos información de tu orden de pago.',
        cta: 'Volver al Inicio'
      },
      orderLabel: 'Número de orden',
      subscriptionPrice: 'Suscripción Anual: $59 USD'
    },
    en: {
      title: 'Payment Result',
      loading: 'Verifying payment status...',
      success: {
        title: 'Payment Successful!',
        description: 'Your annual subscription has been activated.',
        detail: 'You can now access all Organization account features.',
        cta: 'Go to Dashboard'
      },
      pending: {
        title: 'Payment Pending',
        description: 'Your payment is being processed.',
        detail: 'Please wait a moment and refresh this page. If the issue persists, contact us.',
        cta: 'Refresh Status'
      },
      failed: {
        title: 'Payment Not Completed',
        description: 'We could not process your payment.',
        detail: 'You can try again or contact us for assistance.',
        cta: 'Retry Payment',
        logout: 'Back to Home'
      },
      noOrder: {
        title: 'No Payment Information',
        description: 'We could not find your payment order information.',
        cta: 'Back to Home'
      },
      orderLabel: 'Order number',
      subscriptionPrice: 'Annual Subscription: $59 USD'
    }
  };
  
  const t = translations[language];
  
  useEffect(() => {
    if (!orderId) return;
    
    let isMounted = true;
    
    const fetchPaymentStatus = async () => {
      try {
        const response = await paymentsAPI.getStatus(orderId);
        
        if (!isMounted) return;
        
        if (response.status === 'paid' || response.status === 'active') {
          setStatus('success');
        } else if (response.status === 'pending' || response.status === 'pending_payment') {
          setStatus('pending');
        } else {
          setStatus('failed');
        }
      } catch (err) {
        if (!isMounted) return;
        console.error('Error checking payment status:', err);
        setError(err.message);
        setStatus('failed');
      }
    };
    
    fetchPaymentStatus();
    
    return () => {
      isMounted = false;
    };
  }, [orderId]);
  
  const handleRefreshStatus = async () => {
    setStatus('loading');
    try {
      const response = await paymentsAPI.getStatus(orderId);
      if (response.status === 'paid' || response.status === 'active') {
        setStatus('success');
      } else if (response.status === 'pending' || response.status === 'pending_payment') {
        setStatus('pending');
      } else {
        setStatus('failed');
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
      setError(err.message);
      setStatus('failed');
    }
  };
  
  const handleRetryPayment = async () => {
    // Navigate back to register to retry
    logout();
    navigate('/register');
  };
  
  const handleGoToDashboard = () => {
    navigate('/organization-dashboard');
  };
  
  const renderContent = () => {
    if (!orderId) {
      return (
        <Card className="w-full max-w-md border-2 border-gray-200">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <AlertCircle className="h-16 w-16 text-yellow-500" />
            </div>
            <CardTitle className="text-2xl">{t.noOrder.title}</CardTitle>
            <CardDescription>{t.noOrder.description}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Link to="/">
              <Button className="bg-primary text-white hover:bg-primary/90">
                {t.noOrder.cta}
              </Button>
            </Link>
          </CardContent>
        </Card>
      );
    }
    
    if (status === 'loading') {
      return (
        <Card className="w-full max-w-md border-2 border-gray-200">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Loader2 className="h-16 w-16 text-primary animate-spin" />
            </div>
            <CardTitle className="text-2xl">{t.title}</CardTitle>
            <CardDescription>{t.loading}</CardDescription>
          </CardHeader>
        </Card>
      );
    }
    
    if (status === 'success') {
      return (
        <Card className="w-full max-w-md border-2 border-green-200 bg-green-50">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle className="h-16 w-16 text-green-500" />
            </div>
            <CardTitle className="text-2xl text-green-700">{t.success.title}</CardTitle>
            <CardDescription className="text-green-600">{t.success.description}</CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-gray-600">{t.success.detail}</p>
            <div className="bg-white p-3 rounded-lg border border-green-200">
              <p className="text-sm text-gray-500">{t.orderLabel}</p>
              <p className="font-mono font-semibold text-gray-700">{orderId}</p>
            </div>
            <Button 
              onClick={handleGoToDashboard}
              className="w-full bg-green-600 text-white hover:bg-green-700"
            >
              {t.success.cta}
            </Button>
          </CardContent>
        </Card>
      );
    }
    
    if (status === 'pending') {
      return (
        <Card className="w-full max-w-md border-2 border-yellow-200 bg-yellow-50">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <Clock className="h-16 w-16 text-yellow-500" />
            </div>
            <CardTitle className="text-2xl text-yellow-700">{t.pending.title}</CardTitle>
            <CardDescription className="text-yellow-600">{t.pending.description}</CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-gray-600">{t.pending.detail}</p>
            <div className="bg-white p-3 rounded-lg border border-yellow-200">
              <p className="text-sm text-gray-500">{t.orderLabel}</p>
              <p className="font-mono font-semibold text-gray-700">{orderId}</p>
            </div>
            <Button 
              onClick={handleRefreshStatus}
              className="w-full bg-yellow-600 text-white hover:bg-yellow-700"
            >
              <Loader2 className="mr-2 h-4 w-4" />
              {t.pending.cta}
            </Button>
          </CardContent>
        </Card>
      );
    }
    
    // Failed state
    return (
      <Card className="w-full max-w-md border-2 border-red-200 bg-red-50">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <XCircle className="h-16 w-16 text-red-500" />
          </div>
          <CardTitle className="text-2xl text-red-700">{t.failed.title}</CardTitle>
          <CardDescription className="text-red-600">{t.failed.description}</CardDescription>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-gray-600">{t.failed.detail}</p>
          {orderId && (
            <div className="bg-white p-3 rounded-lg border border-red-200">
              <p className="text-sm text-gray-500">{t.orderLabel}</p>
              <p className="font-mono font-semibold text-gray-700">{orderId}</p>
            </div>
          )}
          <div className="space-y-2">
            <Button 
              onClick={handleRetryPayment}
              className="w-full bg-red-600 text-white hover:bg-red-700"
            >
              {t.failed.cta}
            </Button>
            <Link to="/" className="block">
              <Button 
                variant="outline"
                className="w-full"
              >
                {t.failed.logout}
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  };
  
  return (
    <div className="min-h-screen bg-sand-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {renderContent()}
    </div>
  );
};

export default PaymentResult;
